<script lang="ts">
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    import { page } from '$app/stores';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { trackMultipleChoiceClick } from '$lib/global/tracking';
    import { getColorByLightness, getColor } from '$lib/helper';
    import { personalizeHtml } from '$lib/personalizeHtml';
    import { advancedLinking, disabledNavigation, quizMetrics } from '$lib/Stores/stores';
    import { getLinkingPreloadTarget, handleLinkingNavigation } from '$lib/utils/linkingNavigation';
    import { getAllUserAnswers, storeUserAnswer } from '$lib/utils/userAnswerStorage';

    import get from 'lodash/get';
    import { onDestroy } from 'svelte';

    import MultipleChoiceQuestionView from './MultipleChoiceQuestion.View.svelte';

    import type { MultipleChoiceQuestionContent } from '$lib/types/Components/MultipleChoiceQuestion/MultipleChoiceQuestionContent';
    import type { Component } from '$lib/types/Components/SharedTypes';
    import type { Field } from '$lib/types/Field';
    import type { Language } from '$lib/types/Language';
    import type { Theme, TransitionEffect } from '$lib/types/Theme';
    import type { MultipleChoiceAnswer } from '$lib/types/tracking';
    import type { TrackingProperty } from '$lib/types/TrackingProperty';

    export let id = '';
    export let content: MultipleChoiceQuestionContent;
    export let childComponents: Component[] = [];
    export let fields: Field[] = [];
    export let theme: Theme;
    export let nextPage = '';
    export let campaignId = '';
    export let campaignVersionId = '';
    export let firstResultPage = '';
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'none';
    export let trackingProperties: TrackingProperty[] = [];
    export let language: Language = 'en';
    export let layout = DEFAULT_LAYOUT;

    let clickedAnswers: MultipleChoiceAnswer[] = [];
    let isResultVisible = false;
    let navigationTimeout: ReturnType<typeof setTimeout> | undefined = undefined;

    const {
        data: { pageIdSlugMap },
    } = $page;

    const button = childComponents.find((child) => child.componentType === 'button');
    const btnBgColor = getColor(
        get(button, 'content.backgroundColor'),
        theme.buttonBackgroundColor,
    );
    const btnColor = getColor(get(button, 'content.color'), getColorByLightness(btnBgColor));

    const btnWysiwygHtml = get(button, 'content.wysiwygHtml') as string | undefined;
    const btnProps = {
        btnId: get(button, '_id'),
        btnWysiwygHtml: personalizeHtml(btnWysiwygHtml, {
            trackingProperties,
            campaignId,
            language,
        }),
        btnBgColor,
        btnColor,
        btnSize: get(button, 'content.size'),
        btnAnimation: get(button, 'content.animation', ''),
        btnAlign: get(button, 'content.align', 'center'),
        btnFullWidthOnDesktop: get(button, 'content.fullWidthOnDesktop', false),
        btnStyle: get(button, 'content.buttonStyle', 'filled'),
    };

    const personalizedHtml = personalizeHtml(content.wysiwygHtml, {
        trackingProperties,
        campaignId,
        language,
    });

    // Navigation
    const originalLogicNextPage = get(button, 'content.linking.pageId', nextPage) as string;
    const linkingComponentId = get(button, 'content.linking.componentId');
    const externalLink = get(button, 'content.linking.url') as string;
    const isExternal = !get(button, 'content.linking.isInternal', true) && !!externalLink;
    const isNewTab = get(button, 'content.linking.isNewTab', false);
    const nextPageNavigator = new NextPageNavigator(
        campaignId,
        firstResultPage,
        pageIdSlugMap,
        trackingProperties,
    );

    onDestroy(() => {
        if (navigationTimeout) {
            clearTimeout(navigationTimeout);
        }
    });

    $: quizResult = $quizMetrics.find((metric) => metric.insightId === content.fieldName);

    $: totalQuizResults = Object.values(quizResult?.aggregates || {}).reduce(
        (acc, curr) => acc + curr,
        0,
    );

    $: answers = childComponents
        .filter((child) => child.componentType === 'questionTextAnswer')
        .map((answer) => {
            const bgColor = getColor(
                get(answer, 'content.backgroundColor'),
                theme.formFieldBackgroundColor,
            );

            const plainText = get(answer, 'content.plainText', '');
            const count = quizResult?.aggregates[plainText] ?? 0;

            const clickedAnswer = clickedAnswers.find((clickedAnswer) => {
                return answer._id === clickedAnswer.id;
            });

            const responsePercentage =
                typeof totalQuizResults === 'number' && !!clickedAnswers?.length
                    ? (count + (clickedAnswer?.id === answer._id ? 1 : 0)) /
                      (totalQuizResults + clickedAnswers.length)
                    : undefined;

            return {
                id: answer._id,
                color: getColor(get(answer, 'content.color'), getColorByLightness(bgColor)),
                selectedColor: btnBgColor,
                bgColor: bgColor,
                showEmoji: get(answer, 'content.showIcon', false),
                emoji: get(answer, 'content.emoji', ':smiley:'),
                icon: get(answer, 'content.icon', ''),
                srcType: get(answer, 'content.srcType', ''),
                iconName: get(answer, 'content.iconName', ''),
                platform: get(answer, 'content.platform', ''),
                iconColor: getColor(get(answer, 'content.iconColor'), theme.buttonBackgroundColor),
                svg: get(answer, 'content.svg', ''),
                isAnimated: get(answer, 'content.isAnimated', false),
                jsonUrl: get(answer, 'content.jsonUrl', null),
                wysiwygHtml: personalizeHtml(answer.content.wysiwygHtml, {
                    trackingProperties,
                    campaignId,
                    language,
                }),
                plainText,
                responsePercentage,
                isResultVisible,
            };
        });

    const handleSubmit = (answerIds: string[]) => {
        const revealDuration = content.revealDuration;
        const selectedAnswers = answerIds
            .map((id) => {
                return answers.find((answer) => answer.id === id) as MultipleChoiceAnswer;
            })
            .filter((answer): answer is MultipleChoiceAnswer => !!answer);

        clickedAnswers = selectedAnswers;

        // Check for undefined or 0 because 0 is a valid revealDuration
        if (revealDuration) {
            isResultVisible = true;

            // Disable navigation in funnel
            disabledNavigation.set(true);
        }

        // Store user answer
        storeUserAnswer({
            campaignVersionId,
            questionId: id,
            answerId: answerIds.join(','),
        });

        // Tracking
        trackMultipleChoiceClick({
            answers: selectedAnswers,
            question: { id, content },
            fields,
            button,
            campaignId,
        });

        navigationTimeout = setTimeout(
            () => {
                // Enable navigation in funnel again
                disabledNavigation.set(false);

                return handleLinkingNavigation({
                    componentId: button?._id ?? '',
                    userAnswers: getAllUserAnswers(campaignVersionId),
                    advancedLinking: $advancedLinking,
                    nextPageNavigator,
                    originalLogicNextPage,
                    nextPage,
                    externalLink: externalLink,
                    isExternal,
                    isNewTab,
                    currentPageId: $page.data.pageId,
                    linkingComponentId,
                });
            },
            revealDuration ? revealDuration * 1000 : 0,
        );
    };

    const handleMouseEnter = () => {
        const { pageId, isExternal: isExternalTarget } = getLinkingPreloadTarget({
            componentId: button?._id || '',
            userAnswers: getAllUserAnswers(campaignVersionId),
            advancedLinking: $advancedLinking,
            nextPageNavigator,
            originalLogicNextPage,
            nextPage,
            isExternal,
        });

        nextPageNavigator.preload(pageId, isExternalTarget);
    };
</script>

<MultipleChoiceQuestionView
    {id}
    wysiwygHtml={personalizedHtml}
    align={content.align}
    color={getColor(content.color, theme.fontColor)}
    box={content.box}
    onSubmit={handleSubmit}
    onMouseEnter={handleMouseEnter}
    {transitionDelay}
    {transitionEffect}
    {answers}
    {layout}
    {language}
    {...btnProps}
/>
